import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Mail, Phone, CalendarDays, Download, KeyRound } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "svv_admin_token";

const fieldLabel = "mb-2 block text-[11px] uppercase tracking-[0.22em] text-forest";
const inputClass =
  "w-full border border-forest/25 bg-cream px-4 py-3.5 text-sm text-ink outline-none transition-colors duration-300 focus:border-gold";

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [showChange, setShowChange] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [changing, setChanging] = useState(false);

  const fetchEnquiries = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${t}` } });
      setEnquiries(res.data.enquiries);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setEnquiries(null);
      } else {
        toast.error("Could not load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchEnquiries(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries(null);
    setPassword("");
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/enquiries/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "svv-enquiries.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Your enquiries file has been downloaded.");
    } catch (e) {
      toast.error("Could not download the enquiries file.");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChanging(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { current_password: currentPw, new_password: newPw },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Your password has been changed.");
      setCurrentPw("");
      setNewPw("");
      setShowChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not change the password.");
    } finally {
      setChanging(false);
    }
  };

  return (
    <main data-testid="admin-page">
      <section className="grain relative overflow-hidden bg-forestdeep pb-16 pt-40 text-cream lg:pb-20 lg:pt-48">
        <div className="mx-auto max-w-7xl px-6 sm:px-12">
          <p className="mb-6 text-[11px] uppercase tracking-[0.35em] text-gold">Private</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl" data-testid="admin-heading">
            <MaskedLine delay={0.2}>{token ? "Enquiries" : "Karen's Study"}</MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <span className="gold-rule mt-8 block" />
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-16 sm:px-12 lg:py-24">
        {!token ? (
          <Reveal className="mx-auto max-w-md">
            <form onSubmit={handleLogin} data-testid="admin-login-form" className="border border-forest/15 bg-creamdeep/40 p-10">
              <Lock size={26} className="mx-auto text-mulberry" />
              <p className="font-display mt-5 text-center text-2xl text-forest">This room is for Karen alone.</p>
              <p className="mt-2 text-center text-sm text-slate2">Enter the password to read your enquiries.</p>
              <div className="mt-8">
                <label htmlFor="admin-password" className={fieldLabel}>
                  Password
                </label>
                <input
                  id="admin-password"
                  type="password"
                  data-testid="admin-password-input"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  placeholder="Your password"
                />
              </div>
              {error && (
                <p className="mt-4 text-center text-sm text-mulberry" data-testid="admin-login-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-button"
                className="mt-7 w-full bg-forest px-8 py-4 text-[12px] uppercase tracking-[0.25em] text-cream transition-colors duration-500 hover:bg-mulberry disabled:opacity-60"
              >
                {loading ? "Opening" : "Open the door"}
              </button>
            </form>
          </Reveal>
        ) : (
          <div>
            <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm text-slate2" data-testid="enquiry-count">
                {enquiries ? `${enquiries.length} ${enquiries.length === 1 ? "enquiry" : "enquiries"}` : "Loading"}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={exportCsv}
                  data-testid="admin-export-button"
                  className="inline-flex items-center gap-2 border border-forest/25 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:border-gold"
                >
                  <Download size={13} /> Download all
                </button>
                <button
                  onClick={() => setShowChange(!showChange)}
                  data-testid="admin-change-password-toggle"
                  className="inline-flex items-center gap-2 border border-forest/25 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:border-gold"
                >
                  <KeyRound size={13} /> Change password
                </button>
                <button
                  onClick={() => fetchEnquiries(token)}
                  data-testid="admin-refresh-button"
                  className="inline-flex items-center gap-2 border border-forest/25 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:border-gold"
                >
                  <RefreshCw size={13} /> Refresh
                </button>
                <button
                  onClick={logout}
                  data-testid="admin-logout-button"
                  className="inline-flex items-center gap-2 border border-forest/25 px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:border-mulberry hover:text-mulberry"
                >
                  <LogOut size={13} /> Sign out
                </button>
              </div>
            </div>

            {showChange && (
              <Reveal>
                <form
                  onSubmit={handleChangePassword}
                  data-testid="change-password-form"
                  className="mb-10 border border-forest/15 bg-creamdeep/40 p-8"
                >
                  <p className="font-display text-xl text-forest">Choose a password you will remember.</p>
                  <p className="mt-1 text-sm text-slate2">At least eight characters. It takes effect immediately.</p>
                  <div className="mt-6 grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="current-password" className={fieldLabel}>
                        Current password
                      </label>
                      <input
                        id="current-password"
                        type="password"
                        data-testid="current-password-input"
                        required
                        value={currentPw}
                        onChange={(e) => setCurrentPw(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="new-password" className={fieldLabel}>
                        New password
                      </label>
                      <input
                        id="new-password"
                        type="password"
                        data-testid="new-password-input"
                        required
                        minLength={8}
                        value={newPw}
                        onChange={(e) => setNewPw(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={changing}
                    data-testid="change-password-submit"
                    className="mt-7 bg-forest px-8 py-3.5 text-[12px] uppercase tracking-[0.25em] text-cream transition-colors duration-500 hover:bg-mulberry disabled:opacity-60"
                  >
                    {changing ? "Saving" : "Save new password"}
                  </button>
                </form>
              </Reveal>
            )}

            {enquiries && enquiries.length === 0 && (
              <div className="border border-forest/15 bg-creamdeep/40 p-14 text-center" data-testid="enquiries-empty">
                <p className="font-display text-2xl text-forest">No enquiries yet.</p>
                <p className="mt-3 text-sm text-slate2">When someone writes through the contact page, their words will appear here.</p>
              </div>
            )}

            <div className="space-y-6" data-testid="enquiries-list">
              {enquiries?.map((q, i) => (
                <Reveal key={q.id} delay={Math.min(i * 0.06, 0.3)}>
                  <article className="border border-forest/15 bg-cream p-7 transition-colors duration-300 hover:border-gold" data-testid={`enquiry-card-${q.id}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <h3 className="font-display text-2xl text-forest">{q.name}</h3>
                      <span className="text-[11px] uppercase tracking-[0.2em] text-mulberry">{q.ceremony_type}</span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate2">
                      <a href={`mailto:${q.email}`} className="inline-flex items-center gap-2 text-mulberry hover:text-forest" data-testid={`enquiry-email-${q.id}`}>
                        <Mail size={14} /> {q.email}
                      </a>
                      {q.phone && (
                        <span className="inline-flex items-center gap-2">
                          <Phone size={14} /> {q.phone}
                        </span>
                      )}
                      {(q.timeframe || q.date) && (
                        <span className="inline-flex items-center gap-2">
                          <CalendarDays size={14} /> {q.timeframe || q.date}
                        </span>
                      )}
                    </div>
                    {(q.location || q.referral) && (
                      <div className="mt-2 flex flex-wrap gap-x-8 gap-y-2 text-xs text-slate2/80">
                        {q.location && <span>Location: {q.location}</span>}
                        {q.referral && <span>Heard via: {q.referral}</span>}
                      </div>
                    )}
                    {q.message && (
                      <p className="mt-5 border-l-2 border-gold pl-4 text-sm leading-relaxed text-ink">{q.message}</p>
                    )}
                    <p className="mt-4 text-xs text-slate2/60">
                      Received {new Date(q.created_at).toLocaleString("en-GB", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default Admin;
